/**
 * useBoard — Zustand store for the DOM-based board canvas.
 * Manages shapes, selection, viewport, and localStorage persistence.
 */

import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { enableMapSet } from 'immer'
import type { BoardShape, BoardViewport } from './types'

// Immer benötigt explizite Aktivierung für Map/Set
enableMapSet()

// ── Helpers ──────────────────────────────────────────────────────────────────

function storageKey(projectId: string): string {
  return `orto-board-${projectId}`
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

// ── Style type ───────────────────────────────────────────────────────────────

export interface BoardStyle {
  color: string
  strokeWidth: number
  fill: string
  dash: 'solid' | 'dashed' | 'dotted'
  fontSize: number
}

// ── Store types ──────────────────────────────────────────────────────────────

interface BoardState {
  projectId: string | null
  shapes: Map<string, BoardShape>
  selectedIds: Set<string>
  viewport: BoardViewport
  currentStyle: BoardStyle

  // undo/redo
  _history: string[]
  _redoStack: string[]
  captureSnapshot: () => void
  undo: () => void
  redo: () => void

  // lifecycle
  init: (projectId: string) => void
  save: () => void

  // shape CRUD
  addShape: (shape: Omit<BoardShape, 'id'>) => string
  updateShape: (id: string, patch: Partial<BoardShape>) => void
  deleteShape: (id: string) => void
  moveShape: (id: string, x: number, y: number) => void
  resizeShape: (id: string, w: number, h: number) => void

  // selection
  selectShape: (id: string, additive?: boolean) => void
  clearSelection: () => void

  // viewport
  setViewport: (vp: Partial<BoardViewport>) => void

  // style
  setCurrentStyle: (patch: Partial<BoardStyle>) => void
}

// ── Serialisation helpers (Map / Set are not JSON-friendly) ───────────────────

interface SerializedBoard {
  shapes: BoardShape[]
  viewport: BoardViewport
}

function serialise(state: BoardState): string {
  const data: SerializedBoard = {
    shapes: Array.from(state.shapes.values()),
    viewport: state.viewport,
  }
  return JSON.stringify(data)
}

function deserialise(raw: string): { shapes: Map<string, BoardShape>; viewport: BoardViewport } {
  const data: SerializedBoard = JSON.parse(raw)
  const map = new Map<string, BoardShape>()
  for (const s of data.shapes) map.set(s.id, s)
  return { shapes: map, viewport: data.viewport }
}

// ── Store ────────────────────────────────────────────────────────────────────

export const useBoard = create<BoardState>()(
  immer((set, get) => ({
    projectId: null,
    shapes: new Map<string, BoardShape>(),
    selectedIds: new Set<string>(),
    viewport: { x: 0, y: 0, zoom: 1 },
    currentStyle: { color: '#333', strokeWidth: 2, fill: 'none', dash: 'solid', fontSize: 16 },
    _history: [],
    _redoStack: [],

    // ── undo / redo ──────────────────────────────────────────────────────

    captureSnapshot: () => {
      const state = get()
      const snap = serialise(state)
      set((s) => {
        s._history.push(snap)
        if (s._history.length > 50) s._history.splice(0, s._history.length - 50)
        s._redoStack = []
      })
    },

    undo: () => {
      const state = get()
      if (state._history.length === 0) return
      const currentSnap = serialise(state)
      const prev = state._history[state._history.length - 1]
      const { shapes, viewport } = deserialise(prev)
      set((s) => {
        s._redoStack.push(currentSnap)
        s._history.pop()
        s.shapes = shapes
        s.viewport = viewport
        s.selectedIds = new Set<string>()
      })
    },

    redo: () => {
      const state = get()
      if (state._redoStack.length === 0) return
      const currentSnap = serialise(state)
      const next = state._redoStack[state._redoStack.length - 1]
      const { shapes, viewport } = deserialise(next)
      set((s) => {
        s._history.push(currentSnap)
        s._redoStack.pop()
        s.shapes = shapes
        s.viewport = viewport
        s.selectedIds = new Set<string>()
      })
    },

    // ── lifecycle ────────────────────────────────────────────────────────

    init: (projectId: string) => {
      const key = storageKey(projectId)
      try {
        const raw = localStorage.getItem(key)
        if (raw) {
          const { shapes, viewport } = deserialise(raw)
          set((s) => {
            s.projectId = projectId
            s.shapes = shapes
            s.viewport = viewport
            s.selectedIds = new Set<string>()
          })
          return
        }
      } catch { /* ignore corrupt data */ }
      set((s) => {
        s.projectId = projectId
        s.shapes = new Map<string, BoardShape>()
        s.selectedIds = new Set<string>()
        s.viewport = { x: 0, y: 0, zoom: 1 }
      })
    },

    save: () => {
      const state = get()
      if (!state.projectId) return
      try {
        localStorage.setItem(storageKey(state.projectId), serialise(state))
      } catch { /* quota */ }
    },

    // ── shape CRUD ───────────────────────────────────────────────────────

    addShape: (partial) => {
      get().captureSnapshot()
      const id = generateId()
      const shape: BoardShape = { ...partial, id }
      set((s) => { s.shapes.set(id, shape) })
      return id
    },

    updateShape: (id, patch) => {
      set((s) => {
        const shape = s.shapes.get(id)
        if (!shape) return
        Object.assign(shape, patch)
      })
    },

    deleteShape: (id) => {
      get().captureSnapshot()
      set((s) => {
        s.shapes.delete(id)
        s.selectedIds.delete(id)
      })
    },

    moveShape: (id, x, y) => {
      set((s) => {
        const shape = s.shapes.get(id)
        if (shape) { shape.x = x; shape.y = y }
      })
    },

    resizeShape: (id, w, h) => {
      set((s) => {
        const shape = s.shapes.get(id)
        if (!shape) return
        // Tool-created shapes (draw, arrow, shape, text, image, sticky) allow smaller sizes
        const noMinTypes: string[] = ['draw', 'arrow', 'shape', 'text', 'image', 'sticky']
        const minW = noMinTypes.includes(shape.type) ? 1 : 100
        const minH = noMinTypes.includes(shape.type) ? 1 : 80
        shape.w = Math.max(minW, w)
        shape.h = Math.max(minH, h)
      })
    },

    // ── selection ────────────────────────────────────────────────────────

    selectShape: (id, additive = false) => {
      set((s) => {
        if (additive) {
          if (s.selectedIds.has(id)) {
            s.selectedIds.delete(id)
          } else {
            s.selectedIds.add(id)
          }
        } else {
          s.selectedIds = new Set<string>([id])
        }
      })
    },

    clearSelection: () => {
      set((s) => { s.selectedIds = new Set<string>() })
    },

    // ── viewport ─────────────────────────────────────────────────────────

    setViewport: (vp) => {
      set((s) => {
        if (vp.x !== undefined) s.viewport.x = vp.x
        if (vp.y !== undefined) s.viewport.y = vp.y
        if (vp.zoom !== undefined) s.viewport.zoom = Math.min(5, Math.max(0.1, vp.zoom))
      })
    },

    // ── style ─────────────────────────────────────────────────────────────

    setCurrentStyle: (patch) => {
      set((s) => {
        Object.assign(s.currentStyle, patch)
      })
    },
  })),
)
